/**
 * Web Push subscription helper.
 *
 * Encapsulates the flow:
 *   1. Ensure the browser supports the required APIs
 *   2. Ensure a service worker is registered (production only — in dev CRA
 *      doesn't register the SW, so we register it here on-demand)
 *   3. Request Notification permission (must run inside a user gesture)
 *   4. Fetch the VAPID public key from the backend
 *   5. Call pushManager.subscribe() with the key converted to Uint8Array
 *
 * The returned object is the raw PushSubscription — the caller can call
 * .toJSON() to serialise it for the backend.
 */

const API = process.env.REACT_APP_BACKEND_URL;

/** URL-safe base64 (VAPID format) → Uint8Array */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
};

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * Ensure a service worker is registered and return its registration.
 * In production CRA registers /sw.js automatically; in dev we do it manually
 * so the "Save this search" button works end-to-end during QA.
 */
const ensureServiceWorker = async () => {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
};

export const getExistingSubscription = async () => {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return reg.pushManager.getSubscription();
  } catch {
    return null;
  }
};

/**
 * Request permission (if needed) and subscribe. Returns the raw
 * PushSubscription on success, or throws with a user-friendly message.
 */
export const subscribeToPush = async () => {
  if (!pushSupported()) {
    throw new Error('unsupported');
  }

  // 1. Permission — must be a user gesture, hence the caller wires this
  //    behind a button click.
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') {
    throw new Error('permission-denied');
  }

  // 2. Service worker
  const registration = await ensureServiceWorker();
  await navigator.serviceWorker.ready;

  // 3. Public key
  const res = await fetch(`${API}/api/push/public-key`);
  if (!res.ok) throw new Error('vapid-key-fetch-failed');
  const { publicKey } = await res.json();
  if (!publicKey) throw new Error('vapid-key-missing');

  // 4. Subscribe (or reuse existing)
  let sub = await registration.pushManager.getSubscription();
  if (!sub) {
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  return sub;
};

/**
 * Persist a saved search + subscription on the backend.
 * @param {object} args
 * @param {string} args.email      Optional email (empty string when anonymous)
 * @param {string} args.language   'nl' | 'en'
 * @param {object} args.filters    Serialisable filter snapshot from Properties.js
 * @param {PushSubscription} args.subscription
 */
export const persistSavedSearch = async ({ email, language, filters, subscription }) => {
  const res = await fetch(`${API}/api/saved-searches`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: email || null,
      language,
      filters,
      subscription: subscription.toJSON(),
    }),
  });
  if (!res.ok) {
    let detail = 'save-failed';
    try {
      const j = await res.json();
      detail = j.detail || detail;
    } catch { /* body not JSON */ }
    throw new Error(detail);
  }
  return res.json();
};
